import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, RiskBadge, StatusBadge, Field, Empty } from "@/components/app/ui";
import { api, errMsg, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const IST_KEYS = ["aware_at", "board_report_due"];
const lbl = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) + (IST_KEYS.includes(k) ? " (IST)" : "");

function FieldInput({ k, options, value, onChange, testid }) {
  if (!options) return <Input type={k === "aware_at" ? "datetime-local" : /_date$/.test(k) ? "date" : "text"} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
  const opts = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o} value={o} data-testid={`${testid}-option-${o.toLowerCase().replace(/\W+/g, "-")}`}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function CrudPage({ title, subtitle, path, perm, fields, columns, testPrefix, extraDetail, computed, options = {} }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [facet, setFacet] = useState("All");
  const [edit, setEdit] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = () => api.get(`/${path}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  const facetKey = columns[1];
  const facets = ["All", ...new Set(rows.map((r) => r[facetKey]).filter(Boolean))];
  const shown = useMemo(() => rows.filter((r) => (facet === "All" || r[facetKey] === facet) && JSON.stringify(r).toLowerCase().includes(q.toLowerCase())), [rows, q, facet, facetKey]);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/${path}/${edit.id}`, edit); else await api.post(`/${path}`, edit);
      toast.success("Saved");
      if (detail?.id && detail.id === edit.id) setDetail((await api.get(`/${path}/${edit.id}`)).data);
      setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete ${r[columns[0]]}?`)) return;
    try { await api.delete(`/${path}/${r.id}`); toast.success("Deleted"); setDetail(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const open = async (r) => { const { data } = await api.get(`/${path}/${r.id}`); setDetail(data); };
  const writable = can(user, perm);
  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} actions={writable && <Button onClick={() => setEdit({})} className="bg-blue-600 hover:bg-blue-700" data-testid={`${testPrefix}-add-button`}><Plus className="mr-1.5 h-4 w-4" />Add</Button>} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-64 bg-white pl-8" data-testid={`${testPrefix}-search`} /></div>
        {facets.slice(0, 8).map((f) => (
          <button key={f} onClick={() => setFacet(f)} data-testid={`${testPrefix}-filter-${String(f).toLowerCase().replace(/\W+/g, "-")}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${facet === f ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{f}</button>
        ))}
        <span className="ml-auto text-xs text-slate-500">{shown.length} records · Automated discovery connectors: Coming Soon</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid={`${testPrefix}-table`}>
          <thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr>{columns.map((c) => <th key={c} className="px-4 py-2.5">{lbl(c)}</th>)}<th /></tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="cursor-pointer border-t hover:bg-slate-50" onClick={() => open(r)} data-testid={`${testPrefix}-row-${r.id}`}>
                {columns.map((c) => <td key={c} className="px-4 py-2.5 text-slate-700">{c === "risk" ? <RiskBadge risk={r[c]} /> : <span className="line-clamp-2">{r[c] || "—"}</span>}</td>)}
                <td className="px-2" onClick={(e) => e.stopPropagation()}>
                  {writable && <div className="flex"><Button variant="ghost" size="icon" onClick={() => setEdit(r)} data-testid={`${testPrefix}-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r)} data-testid={`${testPrefix}-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button></div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && <Empty text="No records yet." />}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map((k) => (
              <div key={k} className="space-y-1"><Label className="text-xs">{lbl(k)}</Label>
                <FieldInput k={k} options={options[k]} value={edit?.[k] || ""} onChange={(v) => setEdit((p) => ({ ...p, [k]: v }))} testid={`${testPrefix}-field-${k}`} /></div>
            ))}
          </div>
          <DialogFooter><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid={`${testPrefix}-save-button`}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid={`${testPrefix}-detail`}>
          <SheetHeader><SheetTitle className="font-display">{detail?.[columns[0]]}</SheetTitle></SheetHeader>
          {detail && computed && <div className="mt-4 grid grid-cols-2 gap-4 rounded-md border border-blue-200 bg-blue-50/50 p-3" data-testid={`${testPrefix}-computed`}>{computed.map((k) => <Field key={k} label={lbl(k)}>{detail[k] || "—"}</Field>)}</div>}
          {detail && <div className="mt-4 grid grid-cols-2 gap-4">{fields.map((k) => <Field key={k} label={lbl(k)}>{detail[k]}</Field>)}</div>}
          {detail && extraDetail?.(detail)}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const INV_FIELDS = ["application", "system", "database", "data_store", "data_category", "personal_data_element", "purpose", "owner", "data_principal_category", "processor", "location", "retention", "deletion_method", "access_model"];
const PA_FIELDS = ["activity_name", "business_purpose", "data_principal_category", "personal_data_categories", "collection_source", "application", "storage", "processor", "data_sharing", "transfer", "retention", "deletion", "owner", "risk", "applicable_controls"];
const VEN_FIELDS = ["vendor", "service", "is_processor", "data_categories", "processing_purpose", "contract", "dpa", "security_assessment", "sub_processors", "location", "cross_border", "deletion_process", "risk", "contract_expiry", "review_date"];

export const DataInventory = () => (
  <CrudPage title="Data Inventory" subtitle="Manual inventory of applications, systems and personal data elements. Designed to be populated by future read-only discovery connectors."
    path="inventory" perm="inventory" fields={INV_FIELDS} columns={["application", "data_category", "personal_data_element", "location", "processor", "retention"]} testPrefix="inventory" />
);

export const ProcessingActivities = () => (
  <CrudPage title="Processing Activities" subtitle="Record of processing: purpose, Data Principals, storage, sharing, transfers and retention."
    path="processing" perm="inventory" fields={PA_FIELDS} columns={["activity_name", "data_principal_category", "business_purpose", "application", "transfer", "risk"]} testPrefix="processing" />
);

const VendorFindings = (v) => (
  <div className="mt-6">
    <div className="label-caps mb-2">Vendor-specific findings</div>
    {v.findings?.length ? v.findings.map((f) => (
      <div key={f.id} className="mb-2 rounded-md border p-3 text-sm" data-testid={`vendor-finding-${f.id}`}>
        <div className="flex items-center justify-between"><span className="font-mono text-xs text-slate-500">{f.id} · {f.control_id}</span><div className="flex gap-1"><RiskBadge risk={f.risk} /><StatusBadge status={f.status} /></div></div>
        <div className="mt-1 text-slate-700">{f.finding}</div>
      </div>
    )) : <p className="text-sm text-slate-400">No findings linked to this vendor.</p>}
  </div>
);

export const Vendors = () => (
  <CrudPage title="Vendors / Processors" subtitle="Third parties and Data Processors, contracts, DPAs, locations and risk." path="vendors" perm="vendor"
    fields={VEN_FIELDS} columns={["vendor", "risk", "service", "dpa", "location", "contract_expiry"]} testPrefix="vendors" extraDetail={VendorFindings} />
);

const CNS_FIELDS = ["purpose", "processing_activity", "lawful_basis", "collection_point", "notice_version", "withdrawal_mechanism", "active_consents", "withdrawals_30d", "consent_manager", "status", "owner"];
const DPR_FIELDS = ["request_type", "data_principal", "channel", "received_date", "sla_days", "status", "owner", "identity_verified", "systems_involved", "response_date", "notes"];
const BRE_FIELDS = ["title", "aware_at", "severity", "data_categories", "principals_affected", "systems", "status", "principal_notification", "board_report_status", "owner", "root_cause", "notes"];

const YES_NO = ["Yes", "No"];
const CNS_OPTIONS = { status: ["Active", "Draft", "Under Review", "Requires Legal Review", "Retired"], consent_manager: YES_NO };
const DPR_OPTIONS = {
  request_type: ["Access", "Correction", "Completion", "Updating", "Erasure", "Nomination", "Grievance", "Withdrawal of Consent"],
  status: ["Open", "In Progress", "Awaiting Identity Verification", "On Hold", "Completed", "Rejected", "Closed"],
  identity_verified: ["Yes", "No", "Pending"],
};
const BRE_OPTIONS = {
  severity: ["Low", "Medium", "High", "Critical"],
  status: ["Open", "Investigating", "Contained", "Resolved", "Closed"],
  principal_notification: ["Not Started", "Pending", "Sent", "Not Required — legal review"],
  board_report_status: ["Not Started", "Draft", "Submitted"],
};

export const ConsentRegister = () => (
  <CrudPage title="Consent Register" subtitle="Purpose-level record of consent and other lawful bases, notice versions and withdrawal mechanisms (DPDP Act s.4–6). Use pseudonymised counts — never store raw personal data here."
    path="consents" perm="inventory" fields={CNS_FIELDS} options={CNS_OPTIONS} columns={["purpose", "status", "lawful_basis", "collection_point", "active_consents", "withdrawals_30d"]} testPrefix="consents" />
);

export const RightsRequests = () => (
  <CrudPage title="Rights Requests" subtitle="Track Data Principal requests (access, correction, erasure, nomination, grievance) with response deadlines. Set sla_days to your internal target; statutory periods require legal verification. Dates: YYYY-MM-DD."
    path="rights-requests" perm="assess" fields={DPR_FIELDS} options={DPR_OPTIONS} computed={["due_date", "sla_state"]} columns={["request_type", "status", "data_principal", "received_date", "due_date", "sla_state"]} testPrefix="dpr" />
);

export const BreachRegister = () => (
  <CrudPage title="Breach Register" subtitle="Personal data breach log with a 72-hour countdown from awareness for the detailed report to the Data Protection Board (Rule 7 — confirm with legal). Awareness time is in IST."
    path="breaches" perm="finding" fields={BRE_FIELDS} options={BRE_OPTIONS} computed={["board_report_due", "board_report_state"]} columns={["title", "severity", "status", "aware_at", "board_report_due", "board_report_state"]} testPrefix="breaches" />
);
